import { MatchSuggestion, ManualMatchSearchResult, OwnAccountCandidate, PaginatedTransactionResult, TransactionFilters, TransactionQueryFilters } from "@/types/payments";
import { TransactionApiClient } from "../clients/transactionApiClient";

/**
 * Reduce the UI's full filter-panel state down to the subset the DB query can serve
 * (issue #363 review follow-up). `matched` is dropped — match status is derived per-row, not
 * a stored column, so it stays a client-side/page-scoped filter (see useTransactionFilter.ts).
 * Blank strings are dropped too, so an untouched filter panel reads as "no filters" server-side.
 */
export const toTransactionQueryFilters = (filters: TransactionFilters): TransactionQueryFilters => {
  const queryFilters: TransactionQueryFilters = {};
  if (filters.description.trim()) queryFilters.description = filters.description.trim();
  if (filters.minAmount.trim()) queryFilters.minAmount = filters.minAmount.trim();
  if (filters.maxAmount.trim()) queryFilters.maxAmount = filters.maxAmount.trim();
  if (filters.startDate.trim()) queryFilters.startDate = filters.startDate.trim();
  if (filters.endDate.trim()) queryFilters.endDate = filters.endDate.trim();
  return queryFilters;
};

export class TransactionService {
  /**
   * Get a page of transactions across all of the current user's accounts (issue #363).
   * Reads the persisted store from the DB via the transaction-api; no fallback to mocks.
   */
  async getTransactionsForAllAccounts(
    page: number,
    pageSize: number,
    filters?: TransactionQueryFilters,
  ): Promise<PaginatedTransactionResult> {
    try {
      return await TransactionApiClient.getAllTransactions(page, pageSize, filters);
    } catch (error) {
      console.error("Error getting transactions from API:", error);
      throw error; // Re-throw the error to be handled by the UI
    }
  }

  /**
   * Get a page of transactions for a specific account (issue #363).
   * Reads the persisted store from the DB via the transaction-api; no fallback to mocks.
   */
  async getTransactionsForAccount(
    accountId: string,
    page: number,
    pageSize: number,
    filters?: TransactionQueryFilters,
  ): Promise<PaginatedTransactionResult> {
    try {
      return await TransactionApiClient.getTransactionsForAccount(accountId, page, pageSize, filters);
    } catch (error) {
      console.error(`Error getting transactions for account ${accountId} from API:`, error);
      throw error; // Re-throw the error to be handled by the UI
    }
  }

  /**
   * Get the background sync status for a single account (issue #363 follow-up — connect-time
   * sync). Reads the persisted transaction_sync_state cursor via the transaction-api.
   */
  async getSyncStatus(accountId: string): Promise<{
    status: "pending" | "processing" | "completed" | "failed" | null;
    lastSuccessfulFetchAt: string | null;
  }> {
    try {
      return await TransactionApiClient.getSyncStatus(accountId);
    } catch (error) {
      console.error(`Error getting sync status for account ${accountId} from API:`, error);
      throw error; // Re-throw the error to be handled by the UI
    }
  }

  /**
   * Batched sync status for every one of the active company's bank accounts (FIN-568 —
   * bank-account-importing-indicator) — one call for a per-row "importing" indicator to poll,
   * instead of one getSyncStatus call per row.
   */
  async getSyncStatusForCompany(): Promise<Array<{
    bankAccountId: string;
    status: "pending" | "processing" | "completed" | "failed";
    lastSuccessfulFetchAt: string | null;
    // Retry budget — lets the client distinguish a failure still being retried from one the
    // scheduler has permanently abandoned (FIN-745).
    retryCount: number;
    maxRetries: number;
  }>> {
    try {
      return await TransactionApiClient.getSyncStatusForCompany();
    } catch (error) {
      console.error('Error getting company sync status from API:', error);
      throw error;
    }
  }

  /**
   * Get match suggestions for transactions
   * Fetches from API without fallback to mocks
   */
  async getMatchSuggestions(transactionId: string, accountId: string): Promise<MatchSuggestion[]> {
    try {
      const apiSuggestions = await TransactionApiClient.getMatchSuggestions(transactionId, accountId);
      console.log("Retrieved match suggestions from API:", apiSuggestions);
      return apiSuggestions;
    } catch (error) {
      console.error("Error getting match suggestions from API:", error);
      throw error; // Re-throw the error to be handled by the UI
    }
  }

  /**
   * Precomputed candidate matches for a transaction (issue #457) — receivables for a credit
   * transaction, payables for a debit transaction (issue #497), returned as a documentType-
   * discriminated tagged union (see MatchSuggestion in @/types/payments).
   */
  async getPrecomputedMatchSuggestions(transactionId: string): Promise<MatchSuggestion[]> {
    try {
      return await TransactionApiClient.getPrecomputedMatchSuggestions(transactionId);
    } catch (error) {
      console.error("Error getting precomputed match suggestions from API:", error);
      throw error; // Re-throw the error to be handled by the UI
    }
  }

  /**
   * "Manually Match" free-text search fallback (product-feedback-30-aug.F4, issue #904).
   */
  async searchUnmatchedDocuments(direction: "debit" | "credit", query: string): Promise<ManualMatchSearchResult[]> {
    return TransactionApiClient.searchUnmatchedDocuments(direction, query);
  }

  /** FIN-976 F6: tax account rows to pair a bank transaction with by hand. */
  async listOwnAccountCandidates(bankTransactionId: string): Promise<OwnAccountCandidate[]> {
    return TransactionApiClient.listOwnAccountCandidates(bankTransactionId);
  }

  /** FIN-976 F6: pairs the bank transaction with the chosen tax account row. */
  async confirmOwnAccountTransfer(
    bankTransactionId: string,
    candidate: Pick<OwnAccountCandidate, "legKind" | "legId">,
  ): Promise<{ decisionId: string }> {
    return TransactionApiClient.confirmOwnAccountTransfer(bankTransactionId, candidate);
  }

  /**
   * FIN-1567 PR5 (stub -- real booking is FIN-1575): requests marking (or undoing) one or more
   * personal-account lines as private. Always `{ available: false }` on success.
   */
  async markTransactionsPrivate(
    bankTransactionIds: string[],
    intent: "mark" | "undo",
  ): Promise<{ available: false }> {
    return TransactionApiClient.markTransactionsPrivate(bankTransactionIds, intent);
  }
}
